const HIGHLIGHT_HEADINGS = ['que encontraras', 'contenido', 'incluye', 'caracteristicas'];
const FACT_HEADINGS = ['ficha tecnica', 'informacion del libro', 'detalles del producto'];

const fold = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[¿?¡!:.]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const wordCount = (value) => String(value ?? '').match(/[\p{L}\p{N}]+(?:['’\-][\p{L}\p{N}]+)*/gu)?.length ?? 0;

const headingText = (element) => {
  if (/^H[2-4]$/.test(element.tagName)) return element.textContent.trim();
  if (element.tagName !== 'P') return '';
  const meaningful = [...element.childNodes].filter(
    (node) => node.nodeType !== Node.TEXT_NODE || node.textContent.trim() !== '',
  );
  return meaningful.length === 1 && meaningful[0].nodeName === 'STRONG' ? element.textContent.trim() : '';
};

const headingKind = (element) => {
  const heading = fold(headingText(element));
  if (!heading) return null;
  if (HIGHLIGHT_HEADINGS.some((candidate) => heading.includes(candidate))) return 'highlights';
  if (FACT_HEADINGS.some((candidate) => heading.includes(candidate))) return 'facts';
  return 'editorial';
};

const parseFact = (element) => {
  if (!['P', 'LI', 'DD'].includes(element.tagName)) return null;
  const strong = element.querySelector(':scope > strong');
  const text = element.textContent.replace(/\s+/g, ' ').trim();
  const separator = text.indexOf(':');
  const label =
    strong?.textContent.replace(/:\s*$/, '').trim() ?? (separator > 0 ? text.slice(0, separator).trim() : '');
  if (!label || label.length > 60) return null;
  const value = strong
    ? text
        .slice(strong.textContent.length)
        .replace(/^\s*:\s*/, '')
        .trim()
    : text.slice(separator + 1).trim();
  return value ? { label, value } : null;
};

const existingFacts = (root) => {
  const values = new Map();
  root.querySelectorAll('[data-annur-folio-bibliography] .annur-product-facts > div').forEach((row) => {
    const label = row.querySelector('dt')?.textContent;
    const value = row.querySelector('dd')?.textContent;
    if (label && value) values.set(fold(label), fold(value));
  });
  return values;
};

const appendFact = (list, fact) => {
  const row = document.createElement('div');
  const term = document.createElement('dt');
  const definition = document.createElement('dd');
  term.textContent = fact.label;
  definition.textContent = fact.value;
  definition.dir = 'auto';
  row.append(term, definition);
  list.append(row);
};

const consumeFactContent = (node, list, extras, known) => {
  if (['UL', 'OL'].includes(node.tagName)) {
    const remaining = node.cloneNode(false);
    [...node.children].forEach((child) => {
      const fact = parseFact(child);
      if (!fact) remaining.append(child.cloneNode(true));
      else if (!known.has(fold(fact.label))) {
        appendFact(list, fact);
        known.set(fold(fact.label), fold(fact.value));
      } else if (known.get(fold(fact.label)) !== fold(fact.value)) remaining.append(child.cloneNode(true));
    });
    if (remaining.children.length) extras.append(remaining);
    return;
  }
  const fact = parseFact(node);
  if (!fact) extras.append(node.cloneNode(true));
  else if (!known.has(fold(fact.label))) {
    appendFact(list, fact);
    known.set(fold(fact.label), fold(fact.value));
  } else if (known.get(fold(fact.label)) !== fold(fact.value)) extras.append(node.cloneNode(true));
};

const classify = ({ words, highlights, facts }) => {
  if (words === 0) return facts > 0 ? 'facts_only' : 'empty';
  if (words >= 40 && (highlights >= 2 || facts >= 3)) return 'complete';
  if (words >= 25 || facts >= 3) return 'standard';
  return 'sparse';
};

const enhanceFolio = (root) => {
  if (root.dataset.annurFolioReady === 'true') return;
  const source = root.querySelector('[data-annur-folio-source]');
  const enhanced = root.querySelector('[data-annur-folio-enhanced]');
  if (!source || !enhanced) return;

  const overview = enhanced.querySelector('[data-annur-folio-overview]');
  const overviewPage = enhanced.querySelector('[data-annur-folio-overview-page]');
  const highlights = enhanced.querySelector('[data-annur-folio-highlights-content]');
  const highlightsPage = enhanced.querySelector('[data-annur-folio-highlights]');
  const extras = enhanced.querySelector('[data-annur-folio-extras]');
  const extractedFacts = root.querySelector('[data-annur-folio-extracted-facts]');
  const factsRegion = root.querySelector('[data-annur-folio-facts]');
  const known = existingFacts(root);
  let current = 'overview';

  const sourceNodes = [...source.children];
  if (sourceNodes.length === 0 && source.textContent.trim()) {
    const paragraph = document.createElement('p');
    paragraph.textContent = source.textContent.trim();
    sourceNodes.push(paragraph);
  }

  sourceNodes.forEach((node) => {
    const kind = headingKind(node);
    if (kind === 'highlights' || kind === 'facts') {
      current = kind;
      return;
    }
    if (current === 'highlights') highlights.append(node.cloneNode(true));
    else if (current === 'facts') consumeFactContent(node, extractedFacts, extras, known);
    else overview.append(node.cloneNode(true));
  });

  const highlightItems = highlights.querySelectorAll('li').length;
  const words = wordCount(`${overview.textContent} ${highlights.textContent}`);
  const facts = known.size;
  const state = classify({ words, highlights: highlightItems, facts });
  const hasHighlights = highlights.textContent.trim() !== '';
  const hasOverview = overview.textContent.trim() !== '';
  const hasExtras = extras.textContent.trim() !== '';

  overviewPage.hidden = !hasOverview;
  highlightsPage.hidden = !hasHighlights;
  extras.hidden = !hasExtras;
  factsRegion.hidden = facts === 0 && extractedFacts.children.length === 0;
  root.dataset.contentState = state;
  root.dataset.folioLayout = state === 'complete' && hasOverview && hasHighlights ? 'spread' : 'single';
  source.hidden = true;
  enhanced.hidden = false;
  root.dataset.annurFolioReady = 'true';
};

const init = (scope = document) => scope.querySelectorAll('[data-annur-folio]').forEach(enhanceFolio);

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();

document.addEventListener('shopify:section:load', (event) => init(event.target));
